<script setup>
import AppLayout from '@/layouts/AppLayout.vue'
import PdfButton from '@/components/PdfButton.vue'

const props = defineProps({
  bon:            { type: Object,  required: true },
  client:         { type: Object,  default: () => ({}) },
  can_modify:     { type: Boolean, default: false },
  can_see_costs:  { type: Boolean, default: false },
  date_elements:  { type: Boolean, default: false },
  completed:      { type: Boolean, default: false },
  can_sign_charge:{ type: Boolean, default: false },
  can_sign_client:{ type: Boolean, default: false },
  edit_url:       { type: String,  default: '' },
  pdf_url:        { type: String,  default: '' },
  mailpdf_url:    { type: String,  default: '' },
  bons_url:       { type: String,  default: '/bons' },
})

function fmt(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

function fmtNum(n) {
  if (n == null || n === '') return '—'
  return Number(n).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const statutColor = (code) => {
  if (!code) return 'bg-outline/10 text-outline border-outline/10'
  const c = code.toLowerCase()
  if (c.includes('cours'))  return 'bg-primary/10 text-primary border-primary/20'
  if (c.includes('traité') || c.includes('traite')) return 'bg-tertiary/15 text-tertiary border-tertiary/30'
  return 'bg-outline/10 text-outline border-outline/10'
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3 min-w-0">
        <a :href="bons_url" class="text-outline hover:text-on-surface transition-colors flex-shrink-0">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="font-mono font-bold text-primary text-sm"># {{ bon.numero_str }}</span>
        <span class="text-sm text-on-surface font-medium truncate">{{ bon.nom }}</span>
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-6xl mx-auto space-y-6">

        <!-- ── En-tête + actions ───────────────────────────────────── -->
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div class="flex items-center gap-3 flex-wrap">
              <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">
                Bon # {{ bon.numero_str }}
              </h1>
              <span
                :class="statutColor(bon.type_bon_statut_code)"
                class="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border"
              >{{ bon.statut }}</span>
            </div>
            <p class="text-sm text-outline mt-1">{{ bon.nom }}</p>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <a
              v-if="completed"
              :href="mailpdf_url"
              class="px-4 py-2 rounded-md text-xs font-black uppercase tracking-widest flex items-center gap-2 bg-surface-container border border-outline-variant/20 text-outline hover:text-on-surface hover:bg-surface-container-highest transition-all"
            >
              <span class="material-symbols-outlined text-sm">send</span>
              Envoyer PDF
            </a>
            <PdfButton :url="pdf_url" />
            <a
              :href="edit_url"
              class="machined-sheen px-5 py-2 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
            >
              <span class="material-symbols-outlined text-sm">edit</span>
              Modifier
            </a>
          </div>
        </div>

        <!-- ── Détails ─────────────────────────────────────────────── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Détails du bon</h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-[auto_1fr] divide-y md:divide-y-0 md:divide-x divide-outline-variant/10">

            <!-- Logo + coordonnées client -->
            <div class="p-6 flex flex-col items-center justify-start gap-3 md:min-w-[200px] md:max-w-[260px]">
              <img
                v-if="client.logo_form"
                :src="client.logo_form_is_url ? client.logo_form : `/images/client/${client.logo_form}`"
                alt="Logo"
                class="max-h-20 max-w-[180px] object-contain"
              />
              <span v-else class="material-symbols-outlined text-5xl text-outline/30">corporate_fare</span>
              <div v-if="client.address" class="text-xs text-outline text-center leading-relaxed" v-html="client.address" />
              <div v-if="client.tel" class="text-xs text-outline text-center">
                Tél : {{ client.tel }}
                <span v-if="client.fax"> &nbsp;|&nbsp; Fax : {{ client.fax }}</span>
              </div>
            </div>

            <!-- Infos structurées -->
            <div class="p-6">
              <dl class="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
                <div>
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Création</dt>
                  <dd class="text-sm text-on-surface">{{ fmt(bon.date_creation) }}</dd>
                </div>
                <div v-if="bon.num_ts">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Nº TS</dt>
                  <dd class="text-sm font-mono text-on-surface">{{ bon.num_ts }}</dd>
                </div>
                <div v-if="bon.type_bon">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Type</dt>
                  <dd class="text-sm text-on-surface capitalize">{{ bon.type_bon }}</dd>
                </div>
                <div>
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Projet</dt>
                  <dd class="text-sm text-on-surface">
                    <a :href="`/projets/${bon.projet_id}`" class="font-mono font-bold text-primary hover:brightness-125 transition-all">{{ bon.projet_code }}</a>
                    <span class="text-outline ml-1">{{ bon.projet_nom }}</span>
                  </dd>
                </div>
                <div v-if="bon.charge_nom">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Chargé de projet</dt>
                  <dd class="text-sm text-on-surface">{{ bon.charge_nom }}</dd>
                </div>
                <div v-if="bon.createur_nom">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Créateur</dt>
                  <dd class="text-sm text-on-surface">{{ bon.createur_nom }}</dd>
                </div>
                <div v-if="bon.debut || bon.fin">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Période</dt>
                  <dd class="text-sm text-on-surface">{{ fmt(bon.debut) }} → {{ fmt(bon.fin) }}</dd>
                </div>
                <div v-if="bon.chainage">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Chaînage</dt>
                  <dd class="text-sm font-mono text-on-surface">{{ bon.chainage }}</dd>
                </div>
                <div v-if="bon.description" class="col-span-2 sm:col-span-3">
                  <dt class="text-[10px] font-black uppercase tracking-widest text-outline mb-0.5">Description</dt>
                  <dd class="text-sm text-on-surface">{{ bon.description }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <!-- ── Équipements ─────────────────────────────────────────── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Équipements</h2>
            <a
              v-if="can_modify"
              :href="edit_url + '#equipement'"
              class="text-[10px] font-black uppercase tracking-widest text-outline hover:text-primary transition-colors flex items-center gap-1"
            >
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <div v-if="bon.equipements.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">construction</span>
            Aucun équipement
          </div>
          <table v-else class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Unité</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Équipement</th>
                <template v-if="date_elements">
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-28">Début</th>
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-28">Fin</th>
                </template>
                <template v-if="can_see_costs">
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Taux</th>
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(eq, i) in bon.equipements" :key="i" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-5 py-3 font-mono text-on-surface">{{ eq.quantite }}</td>
                <td class="px-5 py-3 font-mono text-outline">{{ eq.unite }}</td>
                <td class="px-5 py-3 text-on-surface">
                  <span class="font-mono text-xs font-bold text-primary mr-2">{{ eq.equipement_code }}</span>
                  {{ eq.equipement_nom }}
                </td>
                <template v-if="date_elements">
                  <td class="px-5 py-3 text-outline text-xs">{{ fmt(eq.debut) }}</td>
                  <td class="px-5 py-3 text-outline text-xs">{{ fmt(eq.fin) }}</td>
                </template>
                <template v-if="can_see_costs">
                  <td class="px-5 py-3 text-right font-mono text-outline text-xs">{{ fmtNum(eq.taux) }}</td>
                  <td class="px-5 py-3 text-right font-mono text-on-surface font-bold text-xs">{{ fmtNum(eq.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Personnel (type_fonctions) ─────────────────────────── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Personnel</h2>
            <a
              v-if="can_modify"
              :href="edit_url + '#type_fonction'"
              class="text-[10px] font-black uppercase tracking-widest text-outline hover:text-primary transition-colors flex items-center gap-1"
            >
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <div v-if="bon.type_fonctions.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">group</span>
            Aucun personnel
          </div>
          <table v-else class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Unité</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Fonction</th>
                <template v-if="date_elements">
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-28">Début</th>
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-28">Fin</th>
                </template>
                <template v-if="can_see_costs">
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Taux</th>
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(tf, i) in bon.type_fonctions" :key="i" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-5 py-3 font-mono text-on-surface">{{ tf.quantite }}</td>
                <td class="px-5 py-3 font-mono text-outline">{{ tf.unite }}</td>
                <td class="px-5 py-3 text-on-surface">
                  <span class="font-mono text-xs font-bold text-primary mr-2">{{ tf.code }}</span>
                  {{ tf.description }}
                </td>
                <template v-if="date_elements">
                  <td class="px-5 py-3 text-outline text-xs">{{ fmt(tf.debut) }}</td>
                  <td class="px-5 py-3 text-outline text-xs">{{ fmt(tf.fin) }}</td>
                </template>
                <template v-if="can_see_costs">
                  <td class="px-5 py-3 text-right font-mono text-outline text-xs">{{ fmtNum(tf.taux) }}</td>
                  <td class="px-5 py-3 text-right font-mono text-on-surface font-bold text-xs">{{ fmtNum(tf.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Items ──────────────────────────────────────────────── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Items divers</h2>
            <a
              v-if="can_modify"
              :href="edit_url + '#items'"
              class="text-[10px] font-black uppercase tracking-widest text-outline hover:text-primary transition-colors flex items-center gap-1"
            >
              <span class="material-symbols-outlined text-sm">edit</span>Modifier
            </a>
          </div>
          <div v-if="bon.items.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">category</span>
            Aucun item
          </div>
          <table v-else class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-20">Unité</th>
                <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                <template v-if="can_see_costs">
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Taux</th>
                  <th class="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-outline w-24 text-right">Coût</th>
                </template>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/10">
              <tr v-for="(item, i) in bon.items" :key="i" class="hover:bg-surface-container-highest/20 transition-colors">
                <td class="px-5 py-3 font-mono text-on-surface">{{ item.quantite }}</td>
                <td class="px-5 py-3 font-mono text-outline">{{ item.unite }}</td>
                <td class="px-5 py-3 text-on-surface">{{ item.description }}</td>
                <template v-if="can_see_costs">
                  <td class="px-5 py-3 text-right font-mono text-outline text-xs">{{ fmtNum(item.taux) }}</td>
                  <td class="px-5 py-3 text-right font-mono text-on-surface font-bold text-xs">{{ fmtNum(item.cout) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── Commentaires ───────────────────────────────────────── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Commentaires</h2>
          </div>
          <div class="px-6 py-4">
            <pre
              v-if="bon.commentaires"
              class="text-sm text-on-surface font-body whitespace-pre-wrap leading-relaxed"
            >{{ bon.commentaires }}</pre>
            <p v-else class="text-sm text-outline italic">Aucun commentaire</p>
          </div>
        </div>

        <!-- ── Signatures ─────────────────────────────────────────── -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <!-- Chargé de projet -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">
                {{ bon.signature_charge.recipient }}
              </h2>
            </div>
            <div class="p-6 space-y-4">
              <div class="flex flex-col gap-1">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Nom</span>
                <span class="text-sm text-on-surface">{{ bon.signature_charge.nom || '—' }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Courriel</span>
                <span class="text-sm text-on-surface">{{ bon.signature_charge.email || '—' }}</span>
              </div>
              <div class="border border-outline-variant/20 rounded-lg p-4 min-h-[100px] flex items-center justify-center bg-white">
                <img
                  v-if="bon.signature_charge.signed && bon.signature_charge.image"
                  :src="bon.signature_charge.image"
                  alt="Signature"
                  class="max-h-20 max-w-full object-contain"
                />
                <a
                  v-else-if="can_sign_charge"
                  :href="bon.signature_charge.sign_url"
                  class="text-xs font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all flex items-center gap-2"
                >
                  <span class="material-symbols-outlined text-sm">draw</span>Signer
                </a>
                <span v-else class="text-xs text-outline italic">Non signé</span>
              </div>
              <div v-if="bon.signature_charge.date" class="text-xs text-outline">
                {{ bon.signature_charge.date }}
              </div>
            </div>
          </div>

          <!-- Client -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
              <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">
                {{ bon.signature_client.recipient }}
              </h2>
            </div>
            <div class="p-6 space-y-4">
              <div class="flex flex-col gap-1">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Nom</span>
                <span class="text-sm text-on-surface">{{ bon.signature_client.nom || '—' }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-[10px] font-black uppercase tracking-widest text-outline">Courriel</span>
                <span class="text-sm text-on-surface">{{ bon.signature_client.email || '—' }}</span>
              </div>
              <div class="border border-outline-variant/20 rounded-lg p-4 min-h-[100px] flex items-center justify-center bg-white">
                <img
                  v-if="bon.signature_client.signed && bon.signature_client.image"
                  :src="bon.signature_client.image"
                  alt="Signature"
                  class="max-h-20 max-w-full object-contain"
                />
                <a
                  v-else-if="can_sign_client"
                  :href="bon.signature_client.sign_url"
                  class="text-xs font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all flex items-center gap-2"
                >
                  <span class="material-symbols-outlined text-sm">draw</span>Signer
                </a>
                <span v-else class="text-xs text-outline italic">Non signé</span>
              </div>
              <div v-if="bon.signature_client.date" class="text-xs text-outline">
                {{ bon.signature_client.date }}
              </div>
            </div>
          </div>
        </div>

        <!-- ── Photos ─────────────────────────────────────────────── -->
        <div v-if="bon.images.length > 0" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">
              Photos
              <span class="ml-2 text-outline/50">({{ bon.images.length }})</span>
            </h2>
          </div>
          <div class="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <a
              v-for="img in bon.images"
              :key="img.id"
              :href="img.url"
              target="_blank"
              class="group block space-y-2"
            >
              <div class="aspect-square rounded-lg overflow-hidden bg-surface-container-high border border-outline-variant/10 group-hover:border-primary/30 transition-colors">
                <img
                  :src="img.thumbnail_url || img.url"
                  :alt="img.filename"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div v-if="img.description" class="text-[10px] text-outline leading-tight px-0.5">
                {{ img.description }}
              </div>
              <div class="text-[10px] text-outline/60 px-0.5">{{ img.created_at }}</div>
            </a>
          </div>
        </div>

        <!-- Footer -->
        <footer class="pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
